import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  clearStoredAuth0State,
  decodeAuth0State,
  SSO_PROVIDER_CODE_KEY,
} from "../../utils/ssoService";

const SSO_PROVIDER_STORAGE_KEY = "sso:last-provider";

export default function SSOCallback() {
  const [searchParams] = useSearchParams();
  const [isProcessing, setIsProcessing] = useState(true);

  const token = searchParams.get("token");
  const code = searchParams.get("code");
  const error = searchParams.get("error");
  const errorDescription = searchParams.get("error_description");
  const state = searchParams.get("state");

  const decodedState = useMemo(() => decodeAuth0State(state), [state]);
  const postMessageTargetOrigin = decodedState?.origin ?? "*";

  // Handle token
  useEffect(() => {
    if (!token) return;

    // Store the code for redemption after exchange
    if (code) {
      localStorage.setItem(SSO_PROVIDER_CODE_KEY, code);
    }

    const isPopup = window.opener !== null;
    const stateId = decodedState?.id;

    // Resolve provider from stored state or sessionStorage
    let provider = null;
    if (stateId) {
      const { getStoredAuth0State } = require("../../utils/ssoService");
      const storedState = getStoredAuth0State(stateId);
      provider = storedState?.provider ?? null;
    }
    if (!provider) {
      try {
        const storedValue = sessionStorage.getItem(SSO_PROVIDER_STORAGE_KEY);
        if (["google", "microsoft", "apple"].includes(storedValue)) {
          provider = storedValue;
        }
      } catch (storageError) {
        console.warn("Failed to read stored SSO provider", storageError);
      }
    }

    // Clear stored state
    if (stateId) {
      clearStoredAuth0State(stateId);
    }

    const messagePayload = {
      type: "AUTH_TOKEN",
      accessToken: token,
      stateId,
      provider,
    };

    let delivered = false;

    // Try postMessage to opener (popup flow)
    if (isPopup && window.opener) {
      const messageTarget =
        postMessageTargetOrigin === "*" ? "*" : postMessageTargetOrigin;
      window.opener.postMessage(messagePayload, messageTarget);
      delivered = true;
    }

    // Try BroadcastChannel as fallback
    if (!delivered) {
      try {
        const channel = new BroadcastChannel("sso-auth");
        channel.postMessage(messagePayload);
        channel.close();
        delivered = true;
      } catch (broadcastError) {
        console.error("Failed to broadcast SSO token", broadcastError);
      }
    }

    if (delivered) {
      try {
        sessionStorage.removeItem(SSO_PROVIDER_STORAGE_KEY);
      } catch (storageError) {
        console.warn(
          "Failed to clear stored SSO provider in callback",
          storageError,
        );
      }
      setTimeout(() => {
        window.close();
      }, 300);
      return;
    }

    // If not delivered (not a popup and no BroadcastChannel), show error
    setIsProcessing(false);
  }, [code, decodedState, postMessageTargetOrigin, token]);

  // Handle error param from Auth0
  useEffect(() => {
    if (!error) return;

    setIsProcessing(false);
    const message = errorDescription || error;

    if (window.opener) {
      window.opener.postMessage(
        { type: "AUTH_ERROR", error: message },
        postMessageTargetOrigin,
      );
      setTimeout(() => window.close(), 300);
      return;
    }

    // No opener — just show error in page
    console.error("SSO authentication failed:", message);
  }, [error, errorDescription, postMessageTargetOrigin]);

  // Handle missing token and error (unexpected state)
  useEffect(() => {
    if (token || error) return;
    setIsProcessing(false);
  }, [error, token]);

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        minHeight: "100vh",
        fontFamily: '"Poppins", sans-serif',
      }}
    >
      <div style={{ textAlign: "center" }}>
        {isProcessing ? (
          <>
            <div
              style={{
                width: "48px",
                height: "48px",
                border: "3px solid #e5e7eb",
                borderTop: "3px solid #6366f1",
                borderRadius: "50%",
                animation: "spin 0.8s linear infinite",
                margin: "0 auto 16px",
              }}
            />
            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
            <p style={{ fontSize: "14px", color: "#6b7280" }}>
              Processing SSO login...
            </p>
          </>
        ) : error ? (
          <p style={{ fontSize: "14px", color: "#ef4444" }}>
            SSO authentication failed: {errorDescription || error}
          </p>
        ) : (
          <p style={{ fontSize: "14px", color: "#6b7280" }}>Redirecting...</p>
        )}
      </div>
    </div>
  );
}
